import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

// Action type matchers for cross-slice trending invalidation
const isPostMutationFulfilled = (type) =>
  type === 'posts/createPost/fulfilled' ||
  type === 'posts/toggleLike/fulfilled' ||
  type === 'share/toggleShare/fulfilled' ||
  type === 'share/resharePost/fulfilled';

const TRENDING_CACHE_KEY = 'trending_cache_v1';
const TRENDING_CACHE_TTL_MS = 10 * 60 * 1000;

const readCache = () => {
  try {
    const raw = localStorage.getItem(TRENDING_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed) return null;
    return parsed;
  } catch (_) { return null; }
};

const writeCache = (value) => {
  try { localStorage.setItem(TRENDING_CACHE_KEY, JSON.stringify({ ...value, t: Date.now() })); } catch (_) { }
};

export const hydrateTrendingFromCache = createAsyncThunk(
  'trending/hydrateFromCache',
  async () => {
    const cached = readCache();
    if (!cached) return { posts: [], projects: [] };
    if (!cached.t || (Date.now() - cached.t) > TRENDING_CACHE_TTL_MS) {
      return { posts: [], projects: [] };
    }
    return { posts: Array.isArray(cached.posts) ? cached.posts : [], projects: Array.isArray(cached.projects) ? cached.projects : [] };
  }
);

export const setupTrendingListener = createAsyncThunk(
  'trending/setupListener',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      // Query by trendingScore (computed by Cloud Function) with likeCount fallback
      // Items without trendingScore will have trendingScore of 0 and sort by likeCount
      const postsQ = query(collection(db, 'posts'), orderBy('trendingScore', 'desc'), limit(3));
      const projectsQ = query(collection(db, 'projects'), orderBy('trendingScore', 'desc'), limit(3));

      let gotPosts = false;
      let gotProjects = false;

      const maybeSetReady = () => {
        const state = getState();
        if (!state.trending.ready && gotPosts && gotProjects) {
          dispatch(setReady(true));
          // cache on first full ready
          const latest = { posts: state.trending.posts, projects: state.trending.projects };
          writeCache(latest);
        }
      };

      const unsubPosts = onSnapshot(postsQ, (snap) => {
        const arr = snap.docs.map(d => {
          const data = d.data() || {};
          const content = typeof data.content === 'string'
            ? data.content
            : (typeof data.text === 'string'
              ? data.text
              : (typeof data.description === 'string' ? data.description : ''));
          return {
            id: d.id,
            title: data.title || '',
            content,
            location: data.location || '',
            issue: data.issue || null,
            likeCount: data.likeCount || 0,
            trendingScore: data.trendingScore || 0,
            imageUrl: data.imageUrl || '',
            imageUrls: Array.isArray(data.imageUrls) ? data.imageUrls : [],
            userId: data.userId || data.uid || null,
          };
        });
        const withThumb = arr.map(p => ({ ...p, thumbUrl: p.imageUrl || (Array.isArray(p.imageUrls) ? p.imageUrls[0] : '') || '' }));
        dispatch(setTrendingPosts(withThumb));
        gotPosts = true;
        maybeSetReady();
        // refresh cache whenever either side changes
        try { const s = getState(); writeCache({ posts: s.trending.posts, projects: s.trending.projects }); } catch { }
      }, (err) => {
        console.error('trending posts listener error', err);
      });

      const unsubProjects = onSnapshot(projectsQ, (snap) => {
        const arr = snap.docs.map(d => {
          const data = d.data() || {};
          return {
            id: d.id,
            title: data.title || '',
            location: data.location || '',
            likeCount: data.likeCount || 0,
            trendingScore: data.trendingScore || 0,
            imageUrl: data.imageUrl || '',
            heroImage: data.heroImage || '',
            creatorId: data.creatorId || data.userId || null,
          };
        });
        const withThumb = arr.map(pr => ({ ...pr, thumbUrl: pr.imageUrl || pr.heroImage || '' }));
        dispatch(setTrendingProjects(withThumb));
        gotProjects = true;
        maybeSetReady();
        try { const s = getState(); writeCache({ posts: s.trending.posts, projects: s.trending.projects }); } catch { }
      }, (err) => {
        console.error('trending projects listener error', err);
      });

      // Return a combined unsubscribe
      const combinedUnsub = () => {
        try { unsubPosts(); } catch { }
        try { unsubProjects(); } catch { }
      };
      return combinedUnsub;
    } catch (e) {
      return rejectWithValue(e?.message || 'Failed to setup trending listener');
    }
  }
);

export const refreshTrending = createAsyncThunk(
  'trending/refresh',
  async (_, { dispatch, getState }) => {
    // Clean up existing listener
    const state = getState();
    if (state.trending.listener) {
      try { state.trending.listener(); } catch { }
    }
    dispatch(clearTrendingState());
    // Re-setup listeners to force fresh data fetch
    return dispatch(setupTrendingListener()).unwrap();
  }
);

const initialState = {
  posts: [],
  projects: [],
  ready: false,
  stale: false,
  error: null,
  listener: null,
};

const trendingSlice = createSlice({
  name: 'trending',
  initialState,
  reducers: {
    setTrendingPosts: (state, action) => { state.posts = action.payload || []; },
    setTrendingProjects: (state, action) => { state.projects = action.payload || []; },
    setReady: (state, action) => { state.ready = !!action.payload; },
    cleanupTrendingListener: (state) => {
      if (state.listener) {
        try { state.listener(); } catch { }
        state.listener = null;
      }
    },
    clearTrendingState: (state) => {
      state.posts = [];
      state.projects = [];
      state.ready = false;
      state.stale = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateTrendingFromCache.fulfilled, (state, action) => {
        const { posts, projects } = action.payload || {};
        if (Array.isArray(posts) && posts.length && state.posts.length === 0) state.posts = posts;
        if (Array.isArray(projects) && projects.length && state.projects.length === 0) state.projects = projects;
      })
      .addCase(setupTrendingListener.fulfilled, (state, action) => {
        // Store combined unsubscribe
        state.listener = action.payload || null;
      })
      .addCase(setupTrendingListener.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || 'Failed to setup trending listener';
      })
      .addCase(refreshTrending.pending, (state) => {
        state.ready = false;
      })
      .addCase(refreshTrending.fulfilled, (state) => {
        state.ready = true;
      })
      // Listen for post mutations and mark trending as stale (ready=false)
      // The component can then trigger a refresh if needed
      .addMatcher(
        (action) => isPostMutationFulfilled(action.type),
        (state) => {
          // Mark as needing refresh - the component layer can decide to call refreshTrending
          // or we can auto-trigger. For now, mark stale so UI can show indicator.
          state.stale = true;
        }
      );
  }
});

export const {
  setTrendingPosts,
  setTrendingProjects,
  setReady,
  cleanupTrendingListener,
  clearTrendingState,
} = trendingSlice.actions;

export default trendingSlice.reducer;
